// src/pages/Home/HomeDashboard.tsx
import "@/pages/Home/HomeDashboard.scss";

import { Button } from 'react-bootstrap';
import PlannerTasksWidget from "@/widgets/PlannerTasksWidget/PlannerTasksWidget";
import ActivityLogWidget from "@/widgets/ActivityLogWidget/ActivityLogWidget";

import ErrorModal from "@/components/ErrorModal";
import SuccessModal from "@/components/SuccessModal";
import { getMyMemberGroupsCount, getMyOwnedGroupsCount } from '@/api/groupApi';
import { markTaskComplete } from '@/api/dashboardApi';

import type { DashboardSummaryDto } from "@/types/dashboard";
//  import { useInsightsPanel } from "@/contexts/InsightsPanelContext";
import { useNavigate, Link } from "react-router-dom";


import {
  PeopleTeam24Regular,
  PeopleCommunity24Regular,
  TaskListSquareLtr24Regular,
  History24Regular,
} from "@fluentui/react-icons";
import { useEffect, useState } from "react";

interface Props {
  data: DashboardSummaryDto;
  onRefreshActivityLog?: () => Promise<void>;
}

interface SummaryItemProps {
  icon: React.ReactNode;
  label: string;
  value: number | string;
}

function SummaryStatCard({ icon, label, value }: SummaryItemProps) {
  return (
    <div className="home-dashboard__summary-card">
      <div className="home-dashboard__summary-icon">{icon}</div>
      <div className="home-dashboard__summary-content">
        <div className="home-dashboard__summary-value">{value}</div>
        <div className="home-dashboard__summary-label">{label}</div>
      </div>
    </div>
  );
}

export default function HomeDashboard({ data, onRefreshActivityLog }: Props) {
  //  const { insights, notifications, reviews, recentActivity, groupSummary } = data;
  const { insights, recentActivity } = data;
  const navigate = useNavigate();
  
  const [memberCount, setMemberCount] = useState<number | null>(null);
  const [ownerCount, setOwnerCount] = useState<number | null>(null);
  const [plannerTasks, setPlannerTasks] = useState(insights.plannerTasks);
  const [errorModal, setErrorModal] = useState<{ show: boolean; message: string }>({
    show: false,
    message: ''
  });
  const [successModal, setSuccessModal] = useState<{ show: boolean; title: string; message: string }>({
    show: false,
    title: '',
    message: ''
  });

  // Fetch group counts on mount
  useEffect(() => {
    getMyMemberGroupsCount().then(setMemberCount);
    getMyOwnedGroupsCount().then(setOwnerCount);
  }, []);

  const handleTaskComplete = async (taskId: string) => {
    // Find the task to get its title
    const task = plannerTasks.find(t => t.id === taskId);
    await markTaskComplete(taskId, task?.title);
    // Optimistically remove from UI
    setPlannerTasks(prev => prev.filter(task => task.id !== taskId));
    setSuccessModal({
      show: true,
      title: 'Task Completed',
      message: 'Successfully marked the task as complete.'
    });
    // Wait a brief moment for the activity log to be committed to the database
    await new Promise(resolve => setTimeout(resolve, 500));
    // Refresh activity log to show new entry
    await onRefreshActivityLog?.();
  };

  //  const siteCount = groupSummary?.siteCount ?? 0;
  //  const outstandingReviewsCount = groupSummary?.outstandingReviewsCount ?? 0;

  // const { open: openCommunications } = useInsightsPanel();

  return (
    <div className="home-dashboard container-fluid">
      {/* ============================= */}
      {/* SECTION 1 — Header + summary */}
      {/* ============================= */}
      <section className="home-dashboard__shell">
        <div className="home-dashboard__shell-inner">
          <div className="home-dashboard__header-row">
            <div className="home-dashboard__title">
              <h2 className="mb-0">Home</h2>
              <p className="mb-0">Welcome back, Jason. Here's your Microsoft 365 information at a glance.</p>
            </div>
          </div>

          {/* Summary stats row */}
          <div className="home-dashboard__summary-row">
            <SummaryStatCard
              icon={<PeopleTeam24Regular />}
              label="Groups you own"
              value={ownerCount ?? 0}
            />
            <SummaryStatCard
              icon={<PeopleCommunity24Regular />}
              label="Groups you're in"
              value={memberCount ?? 0}
            />
            {/*
            <SummaryStatCard
              icon={<Globe24Regular />}
              label="Sites you use"
              value={siteCount}
            />
            <SummaryStatCard
              icon={<ClipboardTaskListLtr24Regular />}
              label="Reviews to complete"
              value={outstandingReviewsCount}
            />
            */}
          </div>
        </div>
        <div className="text-end mt-2 mb-4">
          <Button
            variant="link"
            className="p-0 text-primary text-decoration-none home-dashboard__all-groups-link"
            onClick={() => navigate("/resources")}
          >
            View all groups →
          </Button>
        </div>
      </section>


      {/* ===================================== */}
      {/* SECTION 2 — Primary cards (Activity Log + Planner Tasks) */}
      {/* ===================================== */}
      <section className="home-dashboard__primary">
        <div className="row g-3">
          <div className="col-lg-6">
            <div className="home-dashboard__card home-dashboard__card--primary">
              <div className="home-dashboard__card-header">
                <TaskListSquareLtr24Regular />
                <div>
                  <span className="home-dashboard__card-header-title">Planner tasks</span>
                  <span className="home-dashboard__card-header-subtitle">Your assigned Planner tasks</span>
                </div>
                {plannerTasks[0]?.webUrl && (
                  <a className="home-dashboard__card-header-link" href={plannerTasks[0].webUrl} target="_blank" rel="noopener noreferrer">
                    View in Planner <span aria-hidden="true">→</span>
                  </a>
                )}
              </div>
              <PlannerTasksWidget tasks={plannerTasks} onTaskComplete={handleTaskComplete} />
            </div>
          </div>

          <div className="col-lg-6">
            <div className="home-dashboard__card home-dashboard__card--primary">
              <Link to="/activity-log" className="home-dashboard__card-header" style={{ textDecoration: 'none', color: 'inherit' }}>
                <History24Regular />
                <div>
                  <span className="home-dashboard__card-header-title">Activity log</span>
                  <span className="home-dashboard__card-header-subtitle">Recent activity in the portal</span>
                </div>
                <span className="home-dashboard__card-header-link">View all activity <span aria-hidden="true">→</span></span>
              </Link>
              <ActivityLogWidget items={recentActivity} />
            </div>
          </div>
        </div>
      </section>

      {/* Error Modal */}
      <ErrorModal
        show={errorModal.show}
        title="Unable to Unfollow Site"
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />

      {/* Success Modal */}
      <SuccessModal
        show={successModal.show}
        title={successModal.title}
        message={successModal.message}
        autoCloseSeconds={4}
        onClose={() => setSuccessModal({ show: false, title: '', message: '' })}
      />
    </div>
  );
}
